import PageSkeleton from "./components/PageSkeleton";

import { useEffect, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/toaster";
import ScrollToTop from "./components/ScrollToTop";
import AIChatSection from "./components/AIChatSection";
import { lazyWithRetry } from "./lib/lazyWithRetry";

// Eagerly load critical page
import Index from "./pages/Index";

// Lazy load all other pages for code splitting
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const About = lazyWithRetry(() => import("./pages/About"));
const Portfolio = lazyWithRetry(() => import("./pages/Portfolio"));
const ProjectDetail = lazyWithRetry(() => import("./pages/ProjectDetail"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Careers = lazyWithRetry(() => import("./pages/Careers"));
const DynamicService = lazyWithRetry(() => import("./pages/services/DynamicService"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const AdminLogin = lazyWithRetry(() => import("./pages/admin/AdminLogin"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogPostPage = lazyWithRetry(() => import("./pages/BlogPost"));
const BlogSubmit = lazyWithRetry(() => import("./pages/BlogSubmit"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const OAuthConsent = lazyWithRetry(() => import("./pages/OAuthConsent"));

import MaintenanceGate from "./components/MaintenanceGate";

// Create a new query client with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Loading component shown during translations loading
const LoadingSpinner = () => <PageSkeleton />;

const App = () => {
  const { i18n } = useTranslation();

  // Update the direction of the page based on the current language
  useEffect(() => {
    const rtlLanguages = ["ar"];
    const direction = rtlLanguages.includes(i18n.language) ? "rtl" : "ltr";
    document.documentElement.dir = direction;
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // Preload translations for performance
  useEffect(() => {
    const preloadLanguages = async () => {
      const languages = ["en", "de", "ar", "tr", "ku"];
      const currentLang = i18n.language;

      // First load current language if not loaded
      if (!i18n.hasResourceBundle(currentLang, "translation")) {
        await i18n.loadLanguages(currentLang);
      }

      // Then load others in background
      for (const lang of languages.filter((l) => l !== currentLang)) {
        if (!i18n.hasResourceBundle(lang, "translation")) {
          i18n
            .loadLanguages(lang)
            .catch((e) => console.error(`Failed to preload ${lang}`, e));
        }
      }
    };

    preloadLanguages();
  }, [i18n]);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<LoadingSpinner />}>
              <MaintenanceGate>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/profile" element={<Profile />} />

                {/* OAuth 2.1 consent screen for connected agent clients */}
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />

                {/* Admin Login */}
                <Route path="/admin/login" element={<AdminLogin />} />

                {/* Company Pages */}
                <Route path="/about" element={<About />} />
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/project/:id" element={<ProjectDetail />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />

                {/* Blog */}
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/submit" element={<BlogSubmit />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />

                {/* Service Pages - Dynamic routing */}
                <Route path="/services/:slug" element={<DynamicService />} />

                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              <AIChatSection />
              </MaintenanceGate>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
